import { PanelLeft, PanelRight, Sparkles } from "lucide-react";
import { getNavItemsForRole } from "@/shared/utils/navigationConfig";
import { useMainStore } from "@/stores";
import { USER_ROLES } from "@/shared/utils/constants";
import SidebarItem from "./SidebarItem";
import SidebarAvatar from "./SidebarAvatar";
import { useSidebarBadges } from "./useSidebarBadges";
import BrandLogo from "@/assets/images/logo.png";
import { Tooltip } from "@heroui/react";
import { useAppMode } from "@/shared/hooks/useAppMode";
import ModeToggle from "@/shared/components/ui/ModeToggle";
import ThemeToggle from "@/shared/components/ui/ThemeToggle";
import AppVersionBadge from "@/shared/components/AppVersionBadge";
import { useWhatsNewStore } from "@/stores/useWhatsNewStore";

const ROLE_BADGE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: "Super Admin",
  [USER_ROLES.SUPER_ADMIN_ALT]: "Super Admin",
  [USER_ROLES.ORG_ADMIN]: "Org Admin",
  [USER_ROLES.USER]: "Member",
};

const Sidebar = ({ expanded, setExpanded }) => {
  const { user } = useMainStore();
  const badgeCounts = useSidebarBadges();
  const unreadUpdates = useWhatsNewStore((state) => state.unread.length);
  const openWhatsNew = useWhatsNewStore((state) => state.open);
  const whatsNew = {
    id: "whats-new",
    title: "What's new",
    tooltip: unreadUpdates > 0 ? `What's new: ${unreadUpdates} new ${unreadUpdates === 1 ? "update" : "updates"}` : "What's new",
    iconType: Sparkles,
    onClick: openWhatsNew,
    highlight: unreadUpdates > 0,
  };
  const { isAdminMode, isOrgAdmin } = useAppMode();
  const navItems = getNavItemsForRole({ user, isAdminMode, isOrgAdmin });

  const bottomNavigation = navItems.filter(
    (item) => item.bottom && item.title !== "Log out",
  );
  const topNavigation = navItems.filter(
    (item) => !item.bottom && item.title !== "Log out",
  );
  const roleBadge = user?.role
    ? (ROLE_BADGE_LABELS[user.role] ?? "Member")
    : null;

  return (
    <div className="bg-white dark:bg-gray-950 h-[100dvh] border-r border-gray-200/80 dark:border-gray-800 flex flex-col justify-between p-3.5 select-none font-sans overflow-hidden">
      {/* Header */}
      <div>
        <div
          className={`flex ${expanded ? "flex-row pb-3.5 border-b border-gray-100 dark:border-gray-800" : "flex-col gap-3 items-center pb-3"
            } justify-between w-full items-center`}
        >
          <div className="flex gap-2 items-center min-w-0">
            <img src={BrandLogo} alt="brand_logo" className="w-[18px] h-[31px] shrink-0" />
            {expanded && roleBadge && (
              <span className="text-[10px] font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-md truncate">
                {roleBadge}
              </span>
            )}
          </div>

          <Tooltip
            content={expanded ? "Collapse Sidebar" : "Expand Sidebar"}
            placement="right"
            offset={12}
            className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium text-xs shadow-md border border-gray-100 dark:border-gray-700 px-3 py-1.5 rounded-lg"
          >
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-secondary/10 dark:hover:bg-secondary/20 hover:border-secondary/30 text-gray-500 dark:text-gray-300 hover:text-secondary dark:hover:text-secondary-light shadow-2xs transition-all duration-150 active:scale-95 cursor-pointer shrink-0"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              {expanded ? (
                <PanelLeft size={18} strokeWidth={1.8} className="transition-colors" />
              ) : (
                <PanelRight size={18} strokeWidth={1.8} className="transition-colors" />
              )}
            </button>
          </Tooltip>
        </div>

        {expanded && isOrgAdmin && (
          <div className="py-2.5">
            <ModeToggle />
          </div>
        )}

        {/* Main Navigation */}
        <div className="mt-3 flex flex-col gap-1.5 transition-all duration-300 overflow-y-auto max-h-[calc(100dvh-250px)] scrollbar-hide">
          {topNavigation.map((nav) => (
            <SidebarItem
              key={nav.id}
              navLink={nav}
              expanded={expanded}
              badge={badgeCounts[nav.title]}
              disabled={nav?.disabled}
            />
          ))}
        </div>
      </div>

      {/* Expanded Bottom Navigation */}
      {expanded && (
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2.5 transition-all duration-300">
          <SidebarItem navLink={whatsNew} expanded={expanded} badge={unreadUpdates} />
          {bottomNavigation.map((nav) => (
            <SidebarItem
              key={nav.id}
              navLink={nav}
              expanded={expanded}
              badge={badgeCounts[nav.title]}
            />
          ))}

          <ThemeToggle compact={!expanded} />
          <SidebarAvatar expanded={expanded} />
          <AppVersionBadge expanded={expanded} />
        </div>
      )}

      {/* Collapsed Bottom Navigation (Theme Toggle + Avatar + Version Badge) */}
      {!expanded && (
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-col items-center gap-2 transition-all duration-300">
          <SidebarItem navLink={whatsNew} expanded={expanded} badge={unreadUpdates} />
          {bottomNavigation.map((nav) => (
            <SidebarItem key={nav.id} navLink={nav} expanded={expanded} badge={badgeCounts[nav.title]} />
          ))}
          <ThemeToggle compact={true} />
          <SidebarAvatar expanded={expanded} />
          <AppVersionBadge expanded={expanded} />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
